import React from 'react';
import { Heart, Users, Award, Globe, Shield } from 'lucide-react';

const BACKEND = process.env.REACT_APP_SERVER_URL || 'http://localhost:5001';

function fullImageUrl(url: string | undefined) {
  if (!url || !url.trim()) return '';
  if (url.startsWith('http')) return url;
  const p = url.startsWith('/') ? url : `/${url}`;
  return `${BACKEND}${p}`;
}

export interface SiteSectionPayload {
  sectionId: string;
  route: string;
  layoutHint: string;
  order: number;
  content: {
    heading?: string;
    subheading?: string;
    body?: string;
    images?: { url?: string; caption?: string }[];
    buttons?: { label?: string; href?: string }[];
    cards?: { title?: string; text?: string; tone?: string }[];
    stats?: { value?: string; label?: string }[];
    seo?: { title?: string; description?: string; keywords?: string };
  };
}

const toneRing: Record<string, string> = {
  blue: 'bg-blue-100 text-blue-600',
  green: 'bg-green-100 text-green-600',
  purple: 'bg-purple-100 text-purple-600',
  orange: 'bg-orange-100 text-orange-600',
};

function IconForCard({ tone }: { tone?: string }) {
  const t = (tone || 'blue').toLowerCase();
  const ring = toneRing[t] || toneRing.blue;
  if (t === 'green') return <div className={`w-16 h-16 ${ring} rounded-full flex items-center justify-center mx-auto mb-4`}><Award className="w-8 h-8" /></div>;
  if (t === 'purple') return <div className={`w-16 h-16 ${ring} rounded-full flex items-center justify-center mx-auto mb-4`}><Users className="w-8 h-8" /></div>;
  if (t === 'orange') return <div className={`w-16 h-16 ${ring} rounded-full flex items-center justify-center mx-auto mb-4`}><Globe className="w-8 h-8" /></div>;
  return <div className={`w-16 h-16 ${ring} rounded-full flex items-center justify-center mx-auto mb-4`}><Heart className="w-8 h-8" /></div>;
}

function BodyParagraphs({ text }: { text?: string }) {
  if (!text) return null;
  const parts = text.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className="space-y-4 text-gray-600 leading-relaxed">
      {parts.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
  );
}

const SectionBlock: React.FC<{ s: SiteSectionPayload }> = ({ s }) => {
  const c = s.content || {};
  const hint = s.layoutHint;

  if (hint === 'about-hero' || hint === 'simple-hero') {
    return (
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          {c.heading && <h1 className="text-4xl md:text-5xl font-bold mb-6">{c.heading}</h1>}
          {c.subheading && <p className="text-lg md:text-xl max-w-3xl mx-auto text-blue-50">{c.subheading}</p>}
          {c.buttons && c.buttons.length > 0 && (
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {c.buttons.map((b, i) => (
                <a
                  key={i}
                  href={b.href || '#'}
                  className="inline-flex items-center rounded-xl bg-white/15 px-5 py-2.5 text-sm font-semibold text-white ring-1 ring-white/30 hover:bg-white/25"
                >
                  {b.label || 'Button'}
                </a>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  if (hint === 'about-mission' || hint === 'centered-text') {
    return (
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            {c.heading && <h2 className="text-3xl font-bold mb-8 text-gray-900">{c.heading}</h2>}
            <BodyParagraphs text={c.body} />
          </div>
        </div>
      </section>
    );
  }

  if (hint === 'about-values') {
    const cards = c.cards || [];
    return (
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          {c.heading && <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">{c.heading}</h2>}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {cards.map((card, i) => (
              <div key={i} className="text-center">
                <IconForCard tone={card.tone} />
                {card.title && <h3 className="text-xl font-semibold mb-2 text-gray-900">{card.title}</h3>}
                {card.text && <p className="text-gray-600">{card.text}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (hint === 'about-story') {
    const img = c.images && c.images[0]?.url ? fullImageUrl(c.images[0].url) : '';
    return (
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {c.heading && <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">{c.heading}</h2>}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                {c.subheading && <h3 className="text-2xl font-semibold mb-4 text-gray-900">{c.subheading}</h3>}
                <BodyParagraphs text={c.body} />
              </div>
              <div className="bg-gray-100 min-h-[280px] rounded-2xl overflow-hidden flex items-center justify-center">
                {img ? (
                  <img src={img} alt={c.images?.[0]?.caption || ''} className="w-full h-full object-cover max-h-[420px]" />
                ) : (
                  <span className="text-gray-500 text-lg px-4 text-center">{c.images?.[0]?.caption || 'Image'}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (hint === 'about-stats') {
    const stats = c.stats || [];
    return (
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {stats.map((st, i) => (
              <div key={i}>
                {st.value && <div className="text-4xl font-bold mb-2">{st.value}</div>}
                {st.label && <div className="text-xl text-blue-100">{st.label}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (hint === 'page-hero-and-body') {
    const bodyText = (c.body && String(c.body).trim()) || '';
    return (
      <div>
        <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-16 md:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              {s.route.includes('privacy') && <Shield className="w-16 h-16 mx-auto mb-4 opacity-90" />}
              {c.heading && <h1 className="text-4xl md:text-5xl font-bold mb-4">{c.heading}</h1>}
              {c.subheading && <p className="text-xl text-blue-100">{c.subheading}</p>}
            </div>
          </div>
        </section>
        {bodyText ? (
          <section className="py-16">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12 prose prose-lg max-w-none text-gray-600">
                <BodyParagraphs text={c.body} />
              </div>
            </div>
          </section>
        ) : null}
      </div>
    );
  }

  return (
    <section className="py-12 border-b border-gray-100">
      <div className="container mx-auto px-4">
        {c.heading && <h2 className="text-2xl font-bold text-gray-900 mb-4">{c.heading}</h2>}
        <BodyParagraphs text={c.body} />
      </div>
    </section>
  );
};

interface Props {
  sections: SiteSectionPayload[];
}

const DynamicPageSections: React.FC<Props> = ({ sections }) => {
  const sorted = [...sections].sort((a, b) => (a.order || 0) - (b.order || 0));

  React.useEffect(() => {
    const firstSeo = sorted.find((s) => s.content?.seo?.title)?.content?.seo;
    if (firstSeo?.title) {
      document.title = firstSeo.title;
    }
    if (firstSeo?.description) {
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'description');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', firstSeo.description);
    }
  }, [sorted]);

  return (
    <div className="dynamic-site-sections">
      {sorted.map((s) => (
        <SectionBlock key={s.sectionId} s={s} />
      ))}
    </div>
  );
};

export default DynamicPageSections;
